
import React from 'react';
import { LocalEvent, ResourceLink } from '../types';
import { Icons } from '../constants';

const LOCAL_RESOURCES: ResourceLink[] = [
  {
    id: 'r1',
    title: 'Trees Columbus, Inc.',
    description: 'Leading non-profit dedicated to planting, preserving, and protecting the tree canopy of Columbus.',
    url: 'https://www.treescolumbus.org/',
    category: 'Non-Profit'
  },
  {
    id: 'r2',
    title: 'Muscogee County UGA Extension',
    description: 'Research-based education for Columbus GA: agriculture and natural resources, family and consumer sciences, and 4-H.',
    url: 'https://extension.uga.edu/county-offices/muscogee.html',
    category: 'Education'
  },
  {
    id: 'r3',
    title: 'Keep Columbus Beautiful Commission',
    description: 'City commission for community beautification, litter prevention, and green space — runs Arbor Day, Help the Hooch, and holiday tree recycling.',
    url: 'https://www.keepcolumbusbeautifulga.org/',
    category: 'Government'
  },
  {
    id: 'r4',
    title: 'Georgia Forestry Commission',
    description: 'Statewide agency providing leadership in protection and conservation of Georgia forest resources.',
    url: 'https://gatrees.org/',
    category: 'Government'
  },
  {
    id: 'r5',
    title: 'GA Power Outage Map',
    description: 'Report tree-related power outages and check restoration status in the Columbus area.',
    url: 'https://www.georgiapower.com/outages-and-storm-center.html',
    category: 'Emergency'
  },
  {
    id: 'r6',
    title: 'Columbus Urban Forestry & Right-of-Way',
    description: 'City division for public tree planting (471 trees scheduled this year), pruning, removal, and storm-debris clearing. Free right-of-way tree planting requests via 311.',
    url: 'https://columbusga.gov/publicworks/Divisions/Urban-Forestry-Right-of-Way',
    category: 'Government'
  },
  {
    id: 'r7',
    title: 'Harris County UGA Extension',
    description: 'Research-based education for Hamilton, Pine Mountain, and Harris County GA — agriculture, natural resources, and 4-H. (706) 628-4824.',
    url: 'https://extension.uga.edu/county-offices/harris.html',
    category: 'Education'
  },
  {
    id: 'r8',
    title: 'Alabama Extension — Russell County (Phenix City)',
    description: 'Serves Phenix City and Russell County AL with forestry, lawn & garden, and 4-H programming. 508 14th St, Phenix City. (334) 298-6845.',
    url: 'https://www.aces.edu/counties/russell/',
    category: 'Education'
  },
  {
    id: 'r9',
    title: 'Chattahoochee Riverkeeper',
    description: 'Nonprofit protecting the Chattahoochee River — organizes cleanup sites across the Columbus area, including Help the Hooch.',
    url: 'https://chattahoochee.org/',
    category: 'Non-Profit'
  },
  {
    id: 'r10',
    title: 'GA Forestry Commission Tree Seedlings',
    description: 'Georgia State Forest Nursery: low-cost seedlings for landowners. Orders open July 1; deliveries begin the first week of December.',
    url: 'https://gatrees.org/forest-management-conservation/tree-seedlings/',
    category: 'Government'
  },
  {
    id: 'r11',
    title: 'Columbus Tree Ordinance (UDO Art. 6, Ch. 4)',
    description: 'Tree protection and removal standards for development in Columbus, GA — codified in the Unified Development Ordinance (Municode).',
    url: 'https://www.municode.com/library/ga/columbus/codes/code_of_ordinances?nodeId=APXAUNDEOR_CH4GEDEST_ART6TRPRRE',
    category: 'Government'
  }
];

// Real, verified events: sourced from the official CCG Insight Newsletter (fetched 2026-09-03)
// and Keep Columbus Beautiful program pages. Do NOT add events without a verifiable source.
const LOCAL_EVENTS: LocalEvent[] = [
  {
    id: 'e1',
    title: "4th Annual KCBC Mayor's Golf Tournament",
    organizer: 'Keep Columbus Beautiful Commission',
    date: '2026-09-25',
    type: 'Meeting',
    description: 'City environmental fundraiser supporting Keep Columbus Beautiful programs. Register through KCBC (706-225-4008).',
    link: 'https://myemail.constantcontact.com/CCG-Insight-Newsletter.html?soid=1107203470899&aid=S0NqXOcpCv8'
  },
  {
    id: 'e2',
    title: '32nd Annual Help the Hooch Cleanup',
    organizer: 'Keep Columbus Beautiful Commission + Columbus Water Works',
    date: '2026-10-02',
    type: 'Cleanup',
    description: 'The Chattahoochee Valley\'s largest tri-city community cleanup (Oct 2–3), with thousands of volunteers across Columbus and Phenix City sites. Register via KCBC.',
    link: 'https://www.keepcolumbusbeautifulga.org/help-the-hooch'
  },
  {
    id: 'e3',
    title: 'Help the Hooch Watershed Festival',
    organizer: 'Keep Columbus Beautiful Commission',
    date: '2026-10-03',
    type: 'Festival',
    description: 'Free family festival after the cleanup: live entertainment, environmental-education exhibits, vendors, and kids’ activities.',
    link: 'https://myemail.constantcontact.com/CCG-Insight-Newsletter.html?soid=1107203470899&aid=S0NqXOcpCv8'
  },
  {
    id: 'e4',
    title: 'Household Hazardous Waste Collection',
    organizer: 'CCG Public Works + Keep Columbus Beautiful Commission',
    date: '2026-10-10',
    type: 'Collection',
    description: 'Drop-off event for household hazardous waste. See KCBC for accepted materials and the collection site.',
    link: 'https://myemail.constantcontact.com/CCG-Insight-Newsletter.html?soid=1107203470899&aid=S0NqXOcpCv8'
  }
];

const CommunityHubView: React.FC = () => {
  return (
    <div className="space-y-12">
      {/* Area Map Context */}
      <div className="bg-bark p-8 rounded-3xl text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-2xl">
        <div className="relative z-10 max-w-2xl">
          <h2 className="text-3xl font-bold mb-2">Chattahoochee Valley Community Hub</h2>
          <p className="opacity-80">Serving Columbus, GA, Phenix City, AL, and the surrounding 50 miles. We specialize in the specific tree species and municipal laws of the Georgia-Alabama border.</p>
          <div className="flex items-center space-x-4 mt-6">
            <div className="bg-gecko px-4 py-2 rounded-lg text-sm font-bold">Seasonal: Fall webworm webs are peaking — see the September notes below</div>
            <div className="text-xs opacity-60">Source: UGA Extension / Moultrie Observer (Aug 2026)</div>
          </div>
        </div>
        <div className="absolute right-0 top-0 opacity-10 transform translate-x-10 -translate-y-10 scale-150 pointer-events-none">
          <Icons.Tree />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Events Column */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-bark flex items-center">
              <span className="w-8 h-8 bg-leaf/20 rounded-lg flex items-center justify-center text-palm mr-3">
                <Icons.Calendar />
              </span>
              Upcoming Local Events
            </h3>
            <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">Columbus & Surrounds</span>
          </div>
          
          <div className="space-y-4">
            {LOCAL_EVENTS.length === 0 ? (
              <div className="bg-white p-6 rounded-2xl border border-stone-200 text-center">
                <p className="text-sm text-stone-500 font-bold">No community events posted yet.</p>
                <p className="text-xs text-stone-400 mt-1">Check back soon — or follow Trees Columbus for city-wide tree events.</p>
                <a href="https://www.treescolumbus.org/" target="_blank" rel="noreferrer" className="inline-block mt-3 px-4 py-2 bg-leaf text-white rounded-xl text-xs font-bold hover:bg-palm transition">Trees Columbus ↗</a>
              </div>
            ) : LOCAL_EVENTS.map(event => (
              <div key={event.id} className="bg-white p-6 rounded-2xl border border-stone-200 shadow-sm hover:border-gecko transition">
                <div className="flex justify-between items-start mb-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    event.type === 'Class' ? 'bg-blue-100 text-blue-700' :
                    event.type === 'Planting' ? 'bg-green-100 text-green-700' :
                    event.type === 'Cleanup' ? 'bg-green-100 text-green-700' :
                    event.type === 'Festival' ? 'bg-amber-100 text-amber-700' :
                    event.type === 'Collection' ? 'bg-purple-100 text-purple-700' :
                    event.type === 'Weather' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-700'
                  }`}>
                    {event.type}
                  </span>
                  <span className="text-sm font-bold text-palm">{new Date(event.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <h4 className="font-bold text-lg mb-1">{event.title}</h4>
                <p className="text-xs text-stone-400 font-bold uppercase mb-2">Organized by {event.organizer}</p>
                <p className="text-sm text-stone-600 mb-4">{event.description}</p>
                <a 
                  href={event.link} 
                  target="_blank" 
                  rel="noreferrer"
                  className="inline-flex items-center text-sm font-bold text-gecko hover:underline"
                >
                  View Event Details 
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Resources Column */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-bark flex items-center">
              <span className="w-8 h-8 bg-leaf/20 rounded-lg flex items-center justify-center text-palm mr-3">
                <Icons.Finance />
              </span>
              Local Resource Directory
            </h3>
            <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">Independent Verification</span>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {LOCAL_RESOURCES.map(resource => (
              <a 
                key={resource.id}
                href={resource.url}
                target="_blank"
                rel="noreferrer"
                className="bg-white p-5 rounded-2xl border border-stone-200 flex items-start space-x-4 hover:bg-stone-50 transition group shadow-sm"
              >
                <div className={`p-3 rounded-xl ${
                  resource.category === 'Emergency' ? 'bg-red-50 text-red-600' :
                  resource.category === 'Government' ? 'bg-blue-50 text-blue-600' : 'bg-stone-50 text-stone-600'
                }`}>
                  <Icons.Safety />
                </div>
                <div>
                  <h5 className="font-bold group-hover:text-palm transition">{resource.title}</h5>
                  <p className="text-xs text-stone-500 mt-1 leading-relaxed">{resource.description}</p>
                  <span className="text-[10px] font-bold text-stone-400 mt-2 block uppercase tracking-tighter">Category: {resource.category}</span>
                </div>
              </a>
            ))}
          </div>

          {/* Local Law Focus */}
<div className="bg-leaf/10 border border-leaf/20 p-6 rounded-2xl">
<h4 className="font-bold text-palm text-sm uppercase tracking-widest mb-3">Columbus Tree Ordinance</h4>
<p className="text-sm text-palm/80 leading-relaxed mb-4">
Tree protection and removal standards for development in Columbus, GA are codified in the Unified Development Ordinance, Article 6, Chapter 4 (Tree Protection and Removal): tree protection and planting plans are required with site-development permits, and tree-density standards apply. For public right-of-way trees, contact the city’s Urban Forestry & Right-of-Way division via 311.
</p>
<a href="https://www.municode.com/library/ga/columbus/codes/code_of_ordinances?nodeId=APXAUNDEOR_CH4GEDEST_ART6TRPRRE" target="_blank" rel="noreferrer" className="inline-block text-sm font-bold text-palm hover:underline bg-white/60 border border-leaf/30 px-4 py-2 rounded-xl transition">Read the Tree Ordinance on Municode ↗</a>
</div>
        </div>
              {/* Seasonal Notes — September in the Valley (sourced) */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <h3 className="text-xl font-bold text-bark flex items-center">
              <span className="w-8 h-8 bg-leaf/20 rounded-lg flex items-center justify-center text-palm mr-3">
                <Icons.Tree />
              </span>
              September in the Valley — Seasonal Tree Notes
            </h3>
            <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">Sources: UGA Extension · NOAA · GFC</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: 'Fall webworm webs are peaking',
                body: 'Silken webs over branch tips are common now on 600+ host species. On mature trees, pruning out infested limbs is usually enough — natural enemies do much of the work, and insecticides are rarely needed.',
                source: 'https://extension.uga.edu/programs-services/landscape-pest-management/Nursery-pests/fall-webworm.html',
                sourceLabel: 'UGA Extension'
              },
              {
                title: 'Fall is the best planting window in Georgia',
                body: 'Cooler air with warm soil lets roots establish before spring. Trees planted November through December put on roots ahead of spring demand — and Georgia Forestry Commission seedling orders are open now, with delivery starting the first week of December.',
                source: 'https://fieldreport.caes.uga.edu/news/fall-is-the-best-time-to-add-new-trees-to-the-landscape/',
                sourceLabel: 'UGA CAES Field Report'
              },
              {
                title: 'Peak of hurricane season is around September 10',
                body: 'Most Atlantic activity falls between mid-August and mid-October. The practical move: schedule pre-storm tree-risk checks now, and know your post-storm hazards before you touch a chainsaw.',
                source: 'https://www.noaa.gov/tropical-cyclone-climatology',
                sourceLabel: 'NOAA'
              },
              {
                title: 'Pecan scab pressure ran high in 2026',
                body: 'UGA pecan specialists reported scab ramping up considerably after late-May rains, with East Georgia more variable than West Georgia.',
                source: 'https://site.extension.uga.edu/pecan/2026/06/2026-mid-season-pecan-update/',
                sourceLabel: 'UGA Pecan Extension (June 2026)'
              }
            ].map((note, i) => (
              <div key={i} className="bg-stone-50 rounded-xl p-5 border border-stone-100">
                <h4 className="font-black text-bark text-sm mb-1.5">{note.title}</h4>
                <p className="text-xs text-stone-600 leading-relaxed mb-2.5">{note.body}</p>
                <a href={note.source} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-gecko hover:underline uppercase tracking-wider">Source: {note.sourceLabel} ↗</a>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-stone-400 mt-4">Every item on this page links to its source. Events are drawn from the official Columbus Consolidated Government newsletter and Keep Columbus Beautiful program pages (verified September 2026).</p>
        </div>
</div>
    </div>
  );
};

export default CommunityHubView;
